import { ReactNode, useRef, useState } from "react";
import { LinkProps } from "/Models/Generated/LinkProps";
import "./AiQuestion.scss";
import {
  Button,
  ButtonWithIcon,
  FocusedLinksBlock,
  GridPresetList,
  GridPresetSidebar,
  GridRow,
  PencilSimpleLineIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from "/App.Components";
import { LinkTargetType } from "/Models/Generated/LinkTargetType";
import { NoradIcon } from "/Models/Generated/NoradIcon";
import { ContentAreaType } from "/Models/Generated/ContentAreaType";
import CopySimpleIcon from "/Components/Icons/CopySimpleIcon";
import { createSpreadableBem } from "/Helpers/createBem";

export interface AiQuestionProps {
  question?: string;
  answer?: string;
  sendEditedQuestion?: (question: string) => void;
  editQuestionButtonText?: string;
  copyButtonText?: string;
  closeButtonText?: string;
  sendButtonText?: string;
  sourcesHeading?: string;
  sources?: LinkProps[];
  finished?: boolean;
  isLast?: boolean;
  sidebarChildren?: ReactNode | ReactNode[] | undefined;
  uid: string;
  feedBackText?: string;
  feedBackSubmittedText?: string;
  recaptchaToken?: string;
}

const AiQuestion = ({
  question,
  sendEditedQuestion,
  editQuestionButtonText,
  copyButtonText,
  sourcesHeading,
  sources,
  finished,
  isLast,
  answer,
  sidebarChildren,
  sendButtonText,
  closeButtonText,
  feedBackText,
  feedBackSubmittedText,
  recaptchaToken,
}: AiQuestionProps) => {
  const [editedQuestion, setEditedQuestion] = useState(question ?? "");
  const [editOpen, setEditOpen] = useState(false);
  const [feedBackSubmitted, setFeedBackSubmitted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function sendFeedback(thumbsUp: boolean) {
    setFeedBackSubmitted(true);
    const url = "/api/aichatbotsubmitfeedback";
    const payload = {
      thumbsUp: thumbsUp,
      question: question,
      answer: answer,
      sourcesText: sources?.map((s) => s.href).join(", "),
      reCaptchaToken: recaptchaToken,
    };
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
    } catch (e) {
      console.error(e);
    }
  }

  const thumbsUpPressed = () => {
    sendFeedback(true);
  };

  const thumbsDownPressed = () => {
    sendFeedback(false);
  };

  const bem = createSpreadableBem("ai-question-norad");
  return (
    <div {...bem("")}>
      <GridRow>
        <GridPresetList>
          <div {...bem("__question-container")}>
            <div {...bem("__question-box")}>
              <div {...bem("__question-row")}>
                <div>{question}</div>
                {finished && !editOpen && editQuestionButtonText && (
                  <ButtonWithIcon
                    onClickHandler={() => {
                      setEditOpen(true);
                      window.setTimeout(
                        () => inputRef.current && inputRef.current.focus(),
                        50
                      );
                    }}
                    icon={PencilSimpleLineIcon}
                    iconPlacement="after"
                    text={editQuestionButtonText}
                    lightIconOnDesktop
                    hideTextOnMobile
                  />
                )}
              </div>
              {editOpen && (
                <div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={editedQuestion}
                    onChange={(e) => setEditedQuestion(e.target.value)}
                    {...bem("__text-input")}
                  />

                  <div {...bem("__close-send-buttons")}>
                    <Button
                      label={closeButtonText ?? ""}
                      onClick={() => setEditOpen(false)}
                      style="secondary"
                      size="s"
                    />
                    <Button
                      label={sendButtonText ?? ""}
                      onClick={() => {
                        sendEditedQuestion &&
                          sendEditedQuestion(editedQuestion);
                        setEditOpen(false);
                      }}
                      style="tertiary"
                      size="s"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div {...bem("__answer-box")}>{answer && <div>{answer}</div>}</div>
          {finished && (
            <div>
              <hr />
              <div {...bem("__answer-buttons__wrapper")}>
                <button
                  {...bem("__answer-buttons__copy")}
                  onClick={() => {
                    navigator.clipboard.writeText(answer ?? "");
                    setTimeout(() => {}, 2000);
                  }}
                >
                  {copyButtonText}
                  <CopySimpleIcon size="20" thickness="Light" />
                </button>
                <div {...bem("__answer-buttons__feedback")}>
                  {feedBackSubmitted && feedBackSubmittedText && (
                    <span>{feedBackSubmittedText}</span>
                  )}
                  {!feedBackSubmitted && feedBackText && (
                    <>
                      <span>{feedBackText}</span>
                      <button onClick={thumbsUpPressed}>
                        <ThumbsUpIcon size="20" thickness="Light" />
                      </button>
                      <button onClick={thumbsDownPressed}>
                        <ThumbsDownIcon size="20" thickness="Light" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </GridPresetList>
        {sources && (
          <GridPresetSidebar>
            <FocusedLinksBlock
              heading={sourcesHeading}
              links={sources.map((s) => ({
                ...s,
                linkTargetType: LinkTargetType.Page,
                openInNewTab: false,
              }))}
              icon={NoradIcon.None}
              contentAreaType={ContentAreaType.OneThird}
              overrideContentAreaFullWidthMargin={false}
              isExpandable
            />
            {sidebarChildren && isLast && (
              <div {...bem("__sidebar__children")}>{sidebarChildren}</div>
            )}
          </GridPresetSidebar>
        )}
      </GridRow>
    </div>
  );
};

export default AiQuestion;
